import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  SelectChangeEvent,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../services/api';
import {
  INK,
  FONTS,
  ledgerInputSx,
  ledgerSelectSx,
} from '../../styles/ledger';
import {
  LedgerPage,
  PageHeader,
  FilterBar,
  LedgerCard,
  StatusDot,
  EmptyState,
  HeaderIconBtn,
  ledgerTableHeadSx,
  ledgerRowSx,
} from '../../components/admin/LedgerPrimitives';
import { Refresh as RefreshIcon } from '@mui/icons-material';

const AI_STATUSES = [
  'active',
  'completed',
  'abandoned',
  'failed',
  'failed_carrier_timeout',
  'refunding',
  'refunded',
  'refund_failed',
] as const;
type AiStatus = (typeof AI_STATUSES)[number];

interface AiConversationRow {
  id: number;
  uuid: string;
  user_id: number;
  status: AiStatus;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: string;
  started_at: string;
  completed_at: string | null;
  messages_count: number;
  user: { id: number; first_name: string; last_name: string; email: string };
}
interface PaginatedResponse {
  data: AiConversationRow[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const statusTone = (s: AiStatus): 'active' | 'inactive' | 'warning' | 'info' | 'neutral' => {
  if (s === 'completed' || s === 'refunded') return 'active';
  if (s === 'failed' || s === 'refund_failed') return 'inactive';
  if (s === 'active') return 'info';
  if (s === 'refunding' || s === 'failed_carrier_timeout') return 'warning';
  return 'neutral';
};

const formatUsd = (val: string | number) => `$${parseFloat(String(val)).toFixed(4)}`;
const formatDateTime = (dt: string) => new Date(dt).toLocaleString('en-CA', { hour12: false });

const AiConversationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading, isError, refetch } = useQuery<PaginatedResponse>({
    queryKey: ['admin', 'ai', 'conversations', page, userId, status, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), per_page: '25' });
      if (userId) params.set('user_id', userId);
      if (status) params.set('status', status);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await axiosInstance.get(`/admin/ai/conversations?${params.toString()}`);
      return res.data;
    },
    staleTime: 30_000,
  });

  return (
    <LedgerPage eyebrowFooter={`Almaship · AI agent · conversations · ${String(data?.total ?? 0).padStart(4, '0')} records`}>
      <PageHeader
        eyebrow="AI Agent · Log"
        title={
          <>
            AI conversations
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              .
            </Box>
          </>
        }
        description="Transcripts and token spend for every AI shipment session. All timestamps in UTC."
        actions={
          <HeaderIconBtn title="Refresh" onClick={() => refetch()}>
            {isLoading ? <CircularProgress size={16} sx={{ color: INK.muted }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
          </HeaderIconBtn>
        }
      />

      <FilterBar>
        <TextField
          size="small"
          label="User ID"
          value={userId}
          onChange={(e) => { setUserId(e.target.value); setPage(1); }}
          sx={{ ...ledgerInputSx, minWidth: 140 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, '&.Mui-focused': { color: INK.accent } }}>
            Status
          </InputLabel>
          <Select value={status} label="Status" onChange={(e: SelectChangeEvent) => { setStatus(e.target.value); setPage(1); }} sx={ledgerSelectSx}>
            <MenuItem value="" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>All</MenuItem>
            {AI_STATUSES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>
                {s.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Date from"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true, sx: { fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, '&.Mui-focused': { color: INK.accent } } }}
          sx={ledgerInputSx}
        />
        <TextField
          label="Date to"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true, sx: { fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, '&.Mui-focused': { color: INK.accent } } }}
          sx={ledgerInputSx}
        />
      </FilterBar>

      {isError && (
        <Alert severity="error" sx={{ borderRadius: 0, border: `1px solid ${INK.inactive}`, fontFamily: FONTS.sans, mb: 2 }}>
          Failed to load conversations.
        </Alert>
      )}

      <LedgerCard padded={false}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={ledgerTableHeadSx}>
                <TableCell>Started (UTC)</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Msgs</TableCell>
                <TableCell align="right">Tokens</TableCell>
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 10, borderBottom: 'none' }}>
                    <CircularProgress size={28} sx={{ color: INK.accent }} />
                  </TableCell>
                </TableRow>
              ) : data?.data?.length ? (
                data.data.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => navigate(`/admin/ai/conversations/${row.id}`)}
                    sx={{ ...ledgerRowSx, cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted, whiteSpace: 'nowrap' }}>
                      {formatDateTime(row.started_at)}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                        {row.user?.first_name} {row.user?.last_name}
                      </Typography>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle }}>
                        {row.user?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDot label={row.status.replace(/_/g, ' ')} tone={statusTone(row.status)} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text }}>
                      {row.messages_count}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text }}>
                      {(row.total_input_tokens + row.total_output_tokens).toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text, fontWeight: 600 }}>
                      {formatUsd(row.total_cost_usd)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ borderBottom: 'none' }}>
                    <EmptyState icon={<SearchIcon sx={{ fontSize: 22 }} />} title="No conversations." />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {data && data.last_page > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: `1px solid ${INK.hairline}` }}>
            <Pagination
              count={data.last_page}
              page={page}
              onChange={(_, p) => setPage(p)}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontFamily: FONTS.mono,
                  fontSize: 12,
                  borderRadius: 0.5,
                  color: INK.muted,
                  '&.Mui-selected': { bgcolor: INK.accent, color: INK.surface, '&:hover': { bgcolor: INK.accentHover } },
                },
              }}
            />
          </Box>
        )}
      </LedgerCard>
    </LedgerPage>
  );
};

export default AiConversationsPage;
